"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.flightProductFactory = flightProductFactory;
var _get2 = _interopRequireDefault(require("lodash/get"));
var _set2 = _interopRequireDefault(require("lodash/set"));
var _uniq2 = _interopRequireDefault(require("lodash/uniq"));
var _head2 = _interopRequireDefault(require("lodash/head"));
var _find2 = _interopRequireDefault(require("lodash/find"));
var _split2 = _interopRequireDefault(require("lodash/split"));
var _last2 = _interopRequireDefault(require("lodash/last"));
var _reduce2 = _interopRequireDefault(require("lodash/reduce"));
var _each2 = _interopRequireDefault(require("lodash/each"));
var _assign2 = _interopRequireDefault(require("lodash/assign"));
var _map2 = _interopRequireDefault(require("lodash/map"));
var _filter2 = _interopRequireDefault(require("lodash/filter"));
var _flatMap2 = _interopRequireDefault(require("lodash/flatMap"));
var _compact2 = _interopRequireDefault(require("lodash/compact"));
var _includes2 = _interopRequireDefault(require("lodash/includes"));
var _take2 = _interopRequireDefault(require("lodash/take"));
var _dateFns = require("date-fns");
var _search = require("@tajawal/web-flights-sdk/lib/utils/search.utils");
var _constants = require("@tajawal/web-flights-sdk/lib/constants");
/**
 * flight product factory
 * @param {Object} data plain unformatted flight product from cart's products
 * @param {boolean} isRtl English or Arabic
 * @returns {Object} formatted hotel product
 */
function flightProductFactory(data) {
  const segments = (0, _get2.default)(data, 'options.segment', []);
  const flightLegs = createLegs(data);
  return {
    _id: (0, _get2.default)(data, '_id'),
    id: (0, _get2.default)(data, 'id'),
    addedBy: (0, _get2.default)(data, 'addedBy'),
    additionalData: (0, _get2.default)(data, 'additionalData'),
    allowedPaymentMethods: (0, _get2.default)(data, 'allowedPaymentMethods'),
    category: (0, _get2.default)(data, 'category'),
    code: (0, _get2.default)(data, 'code'),
    createdAt: (0, _get2.default)(data, 'createdAt'),
    displayCurrency: (0, _get2.default)(data, 'displayCurrency'),
    displayCurrencyRate: (0, _get2.default)(data, 'displayCurrencyRate'),
    displayPrice: (0, _get2.default)(data, 'displayPrice'),
    isDomestic: (0, _get2.default)(data, 'additionalData.isDomestic', false),
    isRoundTrip: (0, _search.checkRoundTrip)((0, _get2.default)(data, 'additionalData.query')),
    flightLegs,
    airports: (0, _uniq2.default)(segments.flatMap(segment => {
      const layover = (0, _get2.default)(segment, 'layover', []) || [];
      return [segment.departureAirport.code, segment.arrivalAirport.code, layover && layover.length > 0 ? (0, _get2.default)(layover, '[0].airport') : undefined];
    })).filter(Boolean),
    airlines: (0, _uniq2.default)(segments.map(segment => segment.marketingCarrierId)).filter(Boolean),
    traveller: getTravellers(data, flightLegs),
    name: (0, _get2.default)(data, 'name', ''),
    options: (0, _get2.default)(data, 'options', {}),
    paymentStatus: (0, _get2.default)(data, 'paymentStatus', ''),
    price: (0, _get2.default)(data, 'price', {}),
    qty: (0, _get2.default)(data, 'qty', ''),
    status: (0, _get2.default)(data, 'status', ''),
    supplierName: (0, _get2.default)(data, 'supplierName', ''),
    supplierConfirmationNumber: (0, _get2.default)(data, 'supplierConfirmationNumber', ''),
    supplierPrice: (0, _get2.default)(data, 'supplierPrice', {}),
    supplierStatus: (0, _get2.default)(data, 'supplierStatus', ''),
    type: (0, _get2.default)(data, 'type', ''),
    updatedAt: (0, _get2.default)(data, 'updatedAt', ''),
    valid: (0, _get2.default)(data, 'valid', false),
    vendor: (0, _get2.default)(data, 'options.fare.vendor', ''),
    vendorName: (0, _get2.default)(data, 'vendorName', ''),
    vendorConfirmationNumber: (0, _get2.default)(data, 'vendorConfirmationNumber', ''),
    vendorStatus: (0, _get2.default)(data, 'vendorStatus', ''),
    vendorPrice: (0, _get2.default)(data, 'vendorPrice', {})
  };
}

/**
 * getTravellers
 * @description gets the formatted travellers for the UI
 */
function getTravellers(data, legs) {
  const travellers = (0, _get2.default)(data, 'traveller', []);
  const ssr = (0, _get2.default)(data, 'additionalData.ssr', []);
  return travellers.map(traveller => {
    const travellerType = (0, _get2.default)(traveller, 'additionalData.type');
    const travellerSeatIndex = (0, _get2.default)(traveller, 'additionalData.seatIndex');
    const travellerSsr = (0, _head2.default)(ssr.filter(ssr => (0, _get2.default)(ssr, 'seatIndex') === travellerSeatIndex));
    const luggageMap = travellerType !== 'INF' && (0, _get2.default)(travellerSsr, 'luggage', []).reduce((acc, {
      id,
      selectedOption
    }) => selectedOption && selectedOption.weight ? (0, _set2.default)(acc, id, selectedOption.weight) : acc, {});
    const luggage = Object.entries(luggageMap || {}).map(([legId, weight]) => [weight, (0, _find2.default)(legs, {
      id: legId
    })]).filter(([weight, leg]) => weight && leg && leg.originId && leg.destinationId).map(([weight, leg]) => [`${weight}KG`, `${leg.originId}-${leg.destinationId}`]);
    const meal = (0, _get2.default)(travellerSsr, 'meal.label');
    const frequentFlyerMembershipNumber = (0, _get2.default)(travellerSsr, 'frequentFlyer.membershipNumber');
    const specialRequest = (0, _get2.default)(travellerSsr, 'specialRequest.label');
    const hasSsr = [luggage.length, meal, frequentFlyerMembershipNumber, specialRequest].some(Boolean);
    return {
      ...traveller,
      hasSsr,
      luggage,
      meal,
      frequentFlyerMembershipNumber,
      specialRequest
    };
  });
}

/**
 * a helper function to create flight product legs
 * @param {Object} data flight product
 * @returns {Array} formatted legs
 */
function createLegs(data) {
  const formattedSegments = (0, _get2.default)(data, 'options.segment', []).map(SegmentFactory);
  const fareObject = {
    requestAirports: (0, _split2.default)((0, _split2.default)((0, _get2.default)(data, 'additionalData.query', ''), '/')[0], '-')
  };
  return (0, _get2.default)(data, 'options.leg', []).map(makeLegFactory(fareObject, formattedSegments)).map((leg, legIndex) => {
    const newSegments = leg.segments.map((segment, segmentIndex) => {
      const legSegmentDetails = (0, _get2.default)(data, `options.fare.legDetails.${legIndex}.segment.${segmentIndex}`);
      return {
        ...segment,
        additionalDetails: legSegmentDetails
      };
    });
    return {
      ...leg,
      segments: newSegments,
      finalSegment: (0, _last2.default)(newSegments),
      segmentsWithTechnicalStops: getSegmentsWithTechnicalStops(newSegments),
      details: (0, _get2.default)(data, `options.fare.legDetails.${legIndex}.price`, [])
    };
  });
}

/**
 * a factory function for flight segments
 * @param {Object} segment raw unformatted segment
 * @returns {Object} formatted segment
 */
function SegmentFactory(segment) {
  const technicalStops = (0, _get2.default)(segment, 'technicalStops');
  const connectingTime = {
    value: (0, _get2.default)(segment, 'connectingTime.value', 0),
    text: (0, _get2.default)(segment, 'connectingTime.text', '')
  };
  const carrierType = (0, _get2.default)(segment, 'carrierType', '');
  const marketingCarrierId = (0, _get2.default)(segment, 'marketingCarrierId', '');
  const operatingCarrierId = (0, _get2.default)(segment, 'operatingCarrierId', '');
  const arrivalCode = (0, _get2.default)(segment, 'arrivalAirport.code', '');
  const departure = (0, _get2.default)(segment, 'departure');
  const departureCode = (0, _get2.default)(segment, 'departureAirport.code', '');
  const flightCode = (0, _get2.default)(segment, 'flightCode', '');
  const layover = (0, _get2.default)(segment, 'layover', []) || [];
  return {
    id: (0, _get2.default)(segment, 'id', ''),
    departureAirport: {
      code: (0, _get2.default)(segment, 'departureAirport.code', ''),
      terminal: (0, _get2.default)(segment, 'departureAirport.terminal', '')
    },
    arrivalAirport: {
      code: (0, _get2.default)(segment, 'arrivalAirport.code', ''),
      terminal: (0, _get2.default)(segment, 'arrivalAirport.terminal', '')
    },
    arrivalCode,
    arrivalTerminal: (0, _get2.default)(segment, 'arrivalAirport.terminal', ''),
    departureCode,
    departureTerminal: (0, _get2.default)(segment, 'departureAirport.terminal', ''),
    mealService: (0, _get2.default)(segment, 'mealService', ''),
    carryOnBaggage: (0, _get2.default)(segment, 'freeBaggage.cabin.text', ''),
    checkInBaggage: (0, _get2.default)(segment, 'freeBaggage.checkIn.text', ''),
    departure,
    departureDateTime: (0, _get2.default)(segment, 'departureDateTime'),
    arrival: (0, _get2.default)(segment, 'arrival', ''),
    arrivalDateTime: (0, _get2.default)(segment, 'arrivalDateTime', ''),
    duration: {
      text: (0, _get2.default)(segment, 'duration.text', '')
    },
    durationText: (0, _get2.default)(segment, 'duration.text', ''),
    marketingCarrierId,
    operatingCarrierId,
    flightCode,
    flightDistance: {
      text: (0, _get2.default)(segment, 'flightDistance.text', '')
    },
    carrierType,
    connectingTime,
    vendorRef: (0, _get2.default)(segment, 'vendorRef'),
    // TODO type?
    layover: layover && layover.length > 0 ? (0, _get2.default)(layover, '[0].airport', layover || connectingTime.text) : layover || connectingTime.text,
    flightNumber: (0, _get2.default)(segment, 'flightNumber', ''),
    aircraftType: (0, _get2.default)(_constants.AIRCRAFT_TYPE, `${carrierType}`, carrierType),
    lockId: [departureCode, arrivalCode, (0, _dateFns.format)(departure, 'YYYYMMDD'), flightCode.replace('-', '')].join(''),
    additionalDetails: {},
    technicalStop: technicalStops && technicalStops.length > 0 ? (0, _find2.default)(segment.layover, {
      airport: (0, _get2.default)(technicalStops, '0.stopDetails.0.locationId')
    }) : null,
    technicalStopSegments: (0, _get2.default)(segment, 'layover'),
    operatedBy: operatingCarrierId && operatingCarrierId !== marketingCarrierId ? operatingCarrierId : null
  };
}

/**
 * a closure function for flight legs factory
 * @param {Object} segment raw unformatted segment
 * @param {Object} segment raw unformatted segment
 * @returns {Function} leg factory
 */
function makeLegFactory(fare, segmentsArg) {
  /**
   * a factory function for flight legs
   * @param {Object} leg raw unformatted leg
   * @returns {Object} formatted leg
   */
  return function LegFactory(leg) {
    const requestAirports = (0, _get2.default)(fare, 'requestAirports', []);
    const segmentId = (0, _get2.default)(leg, 'segmentId', []);
    const segmentsById = segmentId.map(id => (0, _find2.default)(segmentsArg, {
      id
    }) || {});
    const segments = segmentsById.length <= 1 ? segmentsById : segmentsById.map((currentSegment, index) => {
      if (index === 0) return currentSegment;
      const prevSegment = segmentsById[index - 1];
      const layoverStartTime = (0, _dateFns.format)(prevSegment.arrival);
      const layoverEndTime = (0, _dateFns.format)(currentSegment.departure);
      const layoverDurationInSeconds = (0, _dateFns.differenceInSeconds)(layoverEndTime, layoverStartTime);
      const layoverDurationInMinutes = (0, _dateFns.differenceInMinutes)(layoverEndTime, layoverStartTime);
      const hours = Math.floor(layoverDurationInMinutes / 60);
      const displayHours = `${hours}`.padStart(2, '0');
      const minutes = layoverDurationInMinutes % 60;
      const displayMinutes = `${minutes}`.padStart(2, '0');
      return {
        ...currentSegment,
        layoverInSeconds: layoverDurationInSeconds,
        layover: hours > 0 ? `${displayHours}h ${displayMinutes}m` : `${displayMinutes}m`
      };
    });
    const finalSegment = (0, _last2.default)(segments);
    const operatedBySegment = (0, _find2.default)(segments, 'operatedBy');
    const validatingCarrier = (0, _get2.default)(leg, 'validatingCarrier', '');
    const operatingCarriers = (0, _reduce2.default)(segments, (carriers, segment) => {
      const operatingCarrier = (0, _get2.default)(segment, 'operatingCarrierId');
      if (!carriers.includes(operatingCarrier)) {
        return [...carriers, operatingCarrier];
      }
      return carriers;
    }, []);
    const carrier = operatingCarriers.length === 1 && operatingCarriers[0] !== validatingCarrier ? operatingCarriers[0] : validatingCarrier;
    const duration = {
      text: (0, _get2.default)(leg, 'duration.text', '')
    };
    const departure = (0, _get2.default)(leg, 'departure', '');
    const arrival = (0, _get2.default)(leg, 'arrival', '');
    const originId = (0, _get2.default)(leg, 'originId', '');
    const destinationId = (0, _get2.default)(leg, 'destinationId', '');
    const stopId = (0, _get2.default)(leg, 'stopId', []);
    const flags = (0, _get2.default)(leg, 'flags', null);
    const flagsByArrival = (0, _find2.default)(flags, flag => flag.label === 'arrival');
    const flagsByChangeAirport = (0, _find2.default)(flags, flag => flag.label === 'changeAirport');
    const hasChangeAirportFlag = (0, _get2.default)(flagsByChangeAirport, 'value', 0) > 0;
    const stops = stopId.map(stop => {
      const segmentInfo = (0, _find2.default)(segments, {
        departureCode: stop
      });
      return {
        code: stop,
        duration: (0, _get2.default)(segmentInfo, 'layover', null)
      };
    });
    return {
      fare,
      id: (0, _get2.default)(leg, 'id', ''),
      originId,
      originCity: null,
      destinationId,
      destinationCity: null,
      departure,
      departureDateTime: (0, _get2.default)(leg, 'departureDateTime', ''),
      arrival,
      arrivalDateTime: (0, _get2.default)(leg, 'arrivalDateTime', ''),
      validatingCarrier: (0, _get2.default)(leg, 'validatingCarrier', ''),
      segmentId,
      segments,
      carrier,
      path: (0, _get2.default)(leg, 'path', []),
      flightCode: (0, _get2.default)(leg, 'flightCode', ''),
      stopCount: (0, _get2.default)(leg, 'stopCount', 0),
      stopId,
      vendorRef: (0, _get2.default)(leg, 'vendorRef'),
      providerRef: (0, _get2.default)(leg, 'providerRef'),
      flags,
      duration,
      durationText: duration.text,
      lockId: (0, _flatMap2.default)(segments, 'lockId').join('-'),
      finalSegment,
      segmentsExceptLast: (0, _take2.default)(segments, segments.length - 1),
      durationInSeconds: convertDurationStringToSeconds(duration.text),
      departureTime: getUnixTimestamp(leg.departure),
      arrivalTime: getUnixTimestamp((0, _get2.default)(finalSegment, 'arrival')),
      isNearbyOrigin: !(0, _includes2.default)(requestAirports, originId),
      isNearbyDestination: !(0, _includes2.default)(requestAirports, destinationId),
      arrivalFlag: (0, _get2.default)(flagsByArrival, 'value', ''),
      hasChangeAirportFlag,
      stops,
      departureHour: (0, _dateFns.getHours)(departure),
      arrivalHour: (0, _dateFns.getHours)(arrival),
      departureHourMin: (0, _dateFns.format)(departure, 'HH:mm'),
      arrivalHourMin: (0, _dateFns.format)(arrival, 'HH:mm'),
      operatedBy: operatedBySegment ? (0, _get2.default)(operatedBySegment, 'operatedBy') : null,
      ...(hasChangeAirportFlag && getStopsAndInterchangeAirports())
    };
  };

  /**
   * This is a helper taken from sdk-v1 after stripping `dayjs` lib from it.
   * what is does is that it takes a date string in a format like "2020-02-27T22:00:00"
   * and returns the equivalent unix timestamp in seconds
   * @param {string} date
   * @returns {number} number of seconds
   */
  function getUnixTimestamp(date) {
    const dateObject = new Date(date);
    const utcDateInMilliseconds = Date.UTC(dateObject.getFullYear(), dateObject.getMonth(), dateObject.getDate(), dateObject.getHours(), dateObject.getMinutes(), dateObject.getSeconds(), dateObject.getMilliseconds());
    return utcDateInMilliseconds / 1000;
  }

  /**
   * This is a helper taken from sdk-v1 that converts a string
   * from this format "00d 00h 30m" to this => 1800.
   * so basically converting it to number of seconds
   * @param {string} duration
   * @returns {number} number of seconds
   */
  function convertDurationStringToSeconds(duration) {
    if (typeof duration !== 'string') return 0;
    const dayRegex = /(\d+)\s*d/i;
    const hourRegex = /(\d+)\s*h/i;
    const minuteRegex = /(\d+)\s*m/i;
    const secondsPerDay = 24 * 60 * 60;
    const secondsPerHour = 60 * 60;
    const secondsPerMinute = 60;
    return [[dayRegex, secondsPerDay], [hourRegex, secondsPerHour], [minuteRegex, secondsPerMinute]].reduce((result, [regex, numOfSeconds]) => {
      if (regex.test(duration)) {
        const seconds = parseInt(duration.match(regex)[1], 10) * numOfSeconds;
        return result + seconds;
      }
      return result;
    }, 0);
  }

  // In case we have change airport
  // we create a mapping in the leg to define which segments have an exchanged
  // @testme export to a function and add as property
  /** */
  function getStopsAndInterchangeAirports(segments, stops) {
    const interchangeAriports = [];
    (0, _each2.default)(segments, (segment, index) => {
      const from = segment.departureCode;
      const to = segment.arrivalCode;
      if (index > 0) {
        const previousSegment = interchangeAriports[index - 1];
        if (previousSegment.to !== from) {
          interchangeAriports[index - 1] = (0, _assign2.default)(previousSegment, {
            switchAtDestination: true
          });
          interchangeAriports.push({
            from,
            to,
            switchAtOrigin: true
          });
        } else {
          interchangeAriports.push({
            from,
            to
          });
        }
      } else {
        interchangeAriports.push({
          from,
          to
        });
      }
    });

    // @todo optimize and generate while generating stops
    const _stops = (0, _map2.default)(stops, stop => {
      const foundInFrom = (0, _find2.default)(interchangeAriports, {
        from: stop.code,
        switchAtOrigin: true
      });
      const foundInTo = (0, _find2.default)(interchangeAriports, {
        to: stop.code,
        switchAtDestination: true
      });
      return {
        ...stop,
        changeAirport: foundInFrom || foundInTo
      };
    });
    const changeAirportStops = (0, _filter2.default)(_stops, {
      changeAirport: true
    });
    const changeAirportStop = {
      code: (0, _flatMap2.default)(changeAirportStops, 'code'),
      duration: (0, _get2.default)((0, _compact2.default)((0, _flatMap2.default)(changeAirportStops, 'duration')), '0'),
      changeAirport: true
    };
    const newStops = (0, _uniq2.default)((0, _map2.default)(_stops, stop => !stop.changeAirport ? stop : changeAirportStop));
    return {
      interchangeAriports: '',
      stops: newStops
    };
  }
}

/** */
function getSegmentsWithTechnicalStops(segments) {
  return (0, _reduce2.default)(segments, (refactoredSegments, segment) => {
    const technicalStops = (0, _get2.default)(segment, 'technicalStopSegments');
    if (Array.isArray(technicalStops) && technicalStops.length > 0) {
      return [...refactoredSegments, ...generateTechnicalStopSegments(segment, technicalStops)];
    }
    return [...refactoredSegments, segment];
  }, []);
}

/** */
function generateTechnicalStopSegments(segment, technicalStops) {
  return (0, _reduce2.default)(technicalStops, (technicalStopSegments, technicalStop) => {
    return [...technicalStopSegments, ...splitTechnicalStopSegments(segment, technicalStop)];
  }, []);
}

/** */
function splitTechnicalStopSegments(segment, technicalStop) {
  const beforeSegment = {
    ...segment,
    durationText: (0, _get2.default)(technicalStop, 'durationBefore.text'),
    arrivalCode: (0, _get2.default)(technicalStop, 'airport'),
    arrival: (0, _get2.default)(technicalStop, 'arrival'),
    arrivalTerminal: null
  };
  const technicalStopSegment = {
    isTechnicalStop: true,
    duration: (0, _get2.default)(technicalStop, 'duration.text'),
    aircraftType: segment.aircraftType
  };
  const afterSegment = {
    ...segment,
    durationText: (0, _get2.default)(technicalStop, 'durationAfter.text'),
    departureCode: (0, _get2.default)(technicalStop, 'airport'),
    departure: (0, _get2.default)(technicalStop, 'departure'),
    departureTerminal: null,
    layover: null // because layover will be applied only to the before segments only
  };

  return [beforeSegment, technicalStopSegment, afterSegment];
}